import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// La pagina «Preparazione della domanda» (pages/Readiness.js, 31a5bd1). Le scelte che nell'originale erano stato di
// React qui sono nell'indirizzo e il server rende la pagina:
// - menu «Progetto» (select:change): ?project=&tab= (il bando del progetto precedente non passa mai al nuovo, audit E02);
//   il menu del bando si disattiva subito, come disabled={!project} mentre il nuovo progetto si carica.
// - menu «Bando di riferimento»: «Mostra tutti…» / «Nascondi…» mostrano o nascondono i bandi fuori territorio senza
//   rileggere; un bando: PUT /api/projects/:id/bando-riferimento {grant_id} (errori ignorati: chi è in sola lettura tiene
//   la scelta per questa visita), poi ?scelto= (senza grant: ?grant= è il link del passo 5, che il server fa passare da
//   bandoDaProporre).
// - una scheda: ?tab=. «Chiedi all'AI» (solo l'amministratore della piattaforma) si apre sul posto senza cambiare
//   l'indirizzo, come l'originale che non la accettava da ?tab= (Readiness.js:57): pannello nascosto fuori dal frame,
//   titolo e sottotitolo dai valori askTitle/askSub; dalla scheda dell'indirizzo si torna allo stesso modo. Aperta, lo
//   dice a ask_controller.js (evento readiness:ask: lo scorrimento in fondo); una rilettura con morph (un'analisi finita,
//   pannello_analisi_controller) la lascia aperta: ask_controller trattiene il pannello, qui dopo turbo:morph si
//   riapplicano schede, titolo e sottotitolo (l'originale non rileggeva la pagina e restava su «Chiedi all'AI»).
// - recomputing (solo con ?ricalcolo=1, dopo un caricamento): GET /api/projects/:id ogni 3000 ms, al massimo 20 volte,
//   finché recomputing è vero; poi la pagina si rilegge senza ?ricalcolo (Turbo.visit con replace), quindi senza
//   l'avviso anche dopo il ventesimo tentativo, come l'originale. Il polling si ferma lasciando la pagina.
const SHOW_ALL = "__show_all__"
const HIDE_OUTSIDE = "__hide_outside__"
const INTERVALLO = 3000
const TENTATIVI = 20

export default class extends Controller {
  static targets = ["bandi", "toggle", "titolo", "sottotitolo", "scheda", "pannello", "pannelloAsk"]
  static values = { projectId: String, grantId: String, tab: String, recomputing: Boolean, askTitle: String, askSub: String }

  connect() {
    this.showOutside = false
    this.dopoMorph = () => { if (this.suAsk) this.mostraAsk(true) }
    document.addEventListener("turbo:morph", this.dopoMorph)
  }

  disconnect() {
    this.fermaPolling()
    document.removeEventListener("turbo:morph", this.dopoMorph)
  }

  progetto(event) {
    const id = event.detail.value
    if (!id || id === this.projectIdValue) return
    this.bandiTargets.forEach((b) => b.querySelector("[data-select-target='trigger']")?.setAttribute("disabled", ""))
    this.fermaPolling()
    const url = new URL("/app/readiness", window.location.origin)
    url.searchParams.set("project", id)
    url.searchParams.set("tab", this.tabValue)
    Turbo.visit(url.toString())
  }

  async bando(event) {
    const v = event.detail.value
    if (v === SHOW_ALL || v === HIDE_OUTSIDE) {
      this.mostraFuori(v === SHOW_ALL)
      this.ripristinaMenu()
      return
    }
    if (!this.projectIdValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(`/api/projects/${encodeURIComponent(this.projectIdValue)}/bando-riferimento`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", ...(token ? { "X-CSRF-Token": token } : {}) },
        body: JSON.stringify({ grant_id: v }),
      })
    } catch (_) {
      // come .catch(() => {}) dell'originale
    }
    this.vai({ scelto: v, grant: null }, "replace")
  }

  scheda(event) {
    const tab = event.params.scheda
    if (!tab) return
    if (tab === this.tabValue) {
      if (this.suAsk) this.mostraAsk(false)
      return
    }
    this.vai({ tab }, "replace")
  }

  chiedi() {
    if (!this.hasPannelloAskTarget || this.suAsk) return
    this.mostraAsk(true)
  }

  mostraAsk(on) {
    if (on) this.testi = [this.titoloTarget.textContent, this.sottotitoloTarget.textContent]
    this.suAsk = on
    this.schedaTargets.forEach((t) => {
      const attiva = on ? t.dataset.readinessSchedaParam === "ask" : t.dataset.readinessSchedaParam === this.tabValue
      t.dataset.state = attiva ? "active" : "inactive"
      t.setAttribute("aria-selected", String(attiva))
    })
    this.pannelloTargets.forEach((p) => { p.hidden = on; p.dataset.state = on ? "inactive" : "active" })
    this.pannelloAskTargets.forEach((p) => { p.hidden = !on; p.dataset.state = on ? "active" : "inactive" })
    const [titolo, sotto] = on ? [this.askTitleValue, this.askSubValue] : this.testi
    this.titoloTarget.textContent = titolo
    this.sottotitoloTarget.textContent = sotto
    if (on) window.dispatchEvent(new CustomEvent("readiness:ask"))
  }

  // ---- fuori territorio -------------------------------------------------------------------------------------------
  mostraFuori(mostra) {
    this.showOutside = mostra
    const menu = this.bandiTargets[0]
    if (!menu) return
    const gruppo = menu.querySelector("[data-testid='grant-group-outside']")
    let visibili = 0
    menu.querySelectorAll("[data-readiness-fuori]").forEach((voce) => {
      const on = mostra || voce.dataset.value === this.grantIdValue
      voce.hidden = !on
      if (on) visibili += 1
    })
    if (gruppo) gruppo.hidden = visibili === 0
    this.toggleTargets.forEach((t) => {
      t.dataset.value = mostra ? HIDE_OUTSIDE : SHOW_ALL
      const testo = t.querySelector("[data-item-text]")
      if (testo) testo.textContent = mostra ? t.dataset.readinessNascondi : t.dataset.readinessMostra
    })
  }

  // il menu ha scritto il valore della voce speciale nel suo campo: torna al bando in uso
  ripristinaMenu() {
    const menu = this.bandiTargets[0]
    if (!menu) return
    const input = menu.querySelector("[data-select-target='input']")
    if (input) input.value = this.grantIdValue
    this.application.getControllerForElementAndIdentifier(menu, "select")?.sync()
  }

  // ---- ricalcolo dopo un caricamento ------------------------------------------------------------------------------
  recomputingValueChanged() {
    if (this.recomputingValue && this.projectIdValue) this.avviaPolling()
    else this.fermaPolling()
  }

  avviaPolling() {
    if (this.timer) return
    const id = this.projectIdValue
    let tries = 0
    this.timer = setInterval(async () => {
      tries += 1
      let recomputing = true
      try {
        const r = await fetch(`/api/projects/${encodeURIComponent(id)}`, { credentials: "same-origin", headers: { Accept: "application/json" } })
        if (r.ok) recomputing = !!(await r.json())?.recomputing
      } catch (_) {
        // una lettura non riuscita conta come tentativo, come l'intervallo dell'originale che continuava
      }
      if (!this.timer || id !== this.projectIdValue) return
      if (!recomputing || tries >= TENTATIVI) {
        this.fermaPolling()
        const url = new URL(window.location.href)
        url.searchParams.delete("ricalcolo")
        Turbo.visit(url.toString(), { action: "replace" })
      }
    }, INTERVALLO)
  }

  fermaPolling() {
    clearInterval(this.timer)
    this.timer = null
  }

  vai(cambi, action) {
    const url = new URL(window.location.href)
    Object.entries(cambi).forEach(([k, v]) => (v === null ? url.searchParams.delete(k) : url.searchParams.set(k, v)))
    if (!url.searchParams.get("project") && this.projectIdValue) url.searchParams.set("project", this.projectIdValue)
    Turbo.visit(url.toString(), { action })
  }
}
